import React from 'react';
import { Zap, Activity, Battery } from 'lucide-react';

const TechnologyHighlight = () => {
  const technologies = [
    {
      icon: Zap,
      title: 'Photothermal Biomodulation',
      description: 'Precision electromagnetic energy application that optimizes cellular function through controlled wavelength exposure and thermal conditioning.'
    },
    {
      icon: Activity,
      title: 'Cellular Activation',
      description: 'Triggers comprehensive cellular responses including enhanced protein synthesis, growth factor release, and exosome production pathways.'
    },
    {
      icon: Battery,
      title: 'Mitochondrial Stimulation',
      description: 'Direct activation of mitochondrial respiratory complexes, exponentially increasing ATP synthesis for accelerated tissue regeneration.'
    }
  ];

  return (
    <section className="py-32 px-6 bg-gradient-to-br from-teal-50/30 via-white to-silver-50/30">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-20 items-center">
          {/* Left: Visual with video */}
          <div className="relative animate-slide-in-left">
            <div className="relative overflow-hidden rounded-3xl shadow-soft-xl">
              <video
                autoPlay
                loop
                muted
                playsInline
                className="w-full h-[700px] object-cover"
              >
                <source src="https://customer-assets.emergentagent.com/job_celltech-showcase/artifacts/bcun2bt4_MCT%20Unit%20Green%20Background%20Render.mp4" type="video/mp4" />
              </video>
              
              {/* Floating stat cards */}
              <div className="absolute top-8 right-8 bg-white/95 backdrop-blur-sm p-6 rounded-2xl shadow-soft-lg">
                <div className="text-4xl font-light gradient-text mb-2">5x</div>
                <p className="text-silver-600 font-light text-sm">Exosome Production</p>
              </div>
              
              <div className="absolute bottom-8 left-8 bg-white/95 backdrop-blur-sm p-6 rounded-2xl shadow-soft-lg">
                <div className="text-4xl font-light gradient-text mb-2">2-3x</div>
                <p className="text-silver-600 font-light text-sm">Growth Factors</p>
              </div>
            </div>
          </div>

          {/* Right: Technology Details */}
          <div className="space-y-10 animate-slide-in-right">
            <div>
              <h2 className="text-5xl md:text-6xl font-light text-gray-900 mb-6 leading-tight">
                Advanced{' '}
                <span className="gradient-text font-light">Technology</span>
              </h2>
              <p className="text-silver-600 text-lg font-light leading-relaxed">
                Our proprietary photothermal biomodulation platform represents the pinnacle of regenerative medicine innovation, delivering unprecedented cellular optimization and therapeutic outcomes.
              </p>
            </div>

            {/* Technology Cards */}
            <div className="space-y-6">
              {technologies.map((tech, index) => {
                const Icon = tech.icon;
                return (
                  <div
                    key={index}
                    className="group p-8 bg-white rounded-2xl shadow-soft hover:shadow-soft-lg transition-all duration-500 hover:scale-[1.02]"
                  >
                    <div className="flex items-start gap-6">
                      <div className="w-14 h-14 bg-gradient-to-br from-teal-50 to-teal-100 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 group-hover:rotate-3 transition-all duration-500">
                        <Icon className="w-7 h-7 text-teal-500" strokeWidth={1.5} />
                      </div>
                      <div>
                        <h3 className="text-xl font-light mb-3 text-gray-900 group-hover:text-teal-600 transition-colors duration-300">
                          {tech.title}
                        </h3>
                        <p className="text-silver-600 font-light text-sm leading-relaxed">
                          {tech.description}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TechnologyHighlight;
